package gr.tech.GRAttestation.pages.admin.users

import androidx.compose.runtime.*
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import gr.tech.GRAttestation.components.layouts.AdminPageLayout
import gr.tech.GRAttestation.components.widgets.AlertManager
import gr.tech.GRAttestation.components.widgets.NavBreadcrumb
import gr.tech.GRAttestation.components.widgets.UserAlert
import gr.tech.GRAttestation.infrastructure.api.*
import gr.tech.GRAttestation.models.storeusers.STORE_USERS_STATUS
import gr.tech.GRAttestation.models.storeusers.StoreAddUsersRequest
import gr.tech.GRAttestation.models.storeusers.StoreUsersDTO
import gr.tech.GRAttestation.models.tables.BasicTableFilters
import gr.tech.GRAttestation.sections.admin.AdminStoreInvitesPanel
import gr.tech.GRAttestation.sections.admin.AdminStoreUsersPanel
import gr.tech.GRAttestation.styles.grstyles.Theme
import gr.tech.GRAttestation.util.Constants
import gr.tech.models.storeusers.StoreUsersInviteDTO
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Button
import org.jetbrains.compose.web.dom.Li
import org.jetbrains.compose.web.dom.Text
import org.jetbrains.compose.web.dom.Ul

@Page()
@Composable
fun AdminUsersPage() {
    AdminPageLayout() { context ->
        AdminUsersScreen(context)
    }
}

@Composable
fun AdminUsersScreen(context: PageContext) {
    val scope = rememberCoroutineScope()
    val alertManager = remember { AlertManager() }
    alertManager.DisplayAlerts()

    var activeTab by remember { mutableStateOf("users") }

    /* All Store Users */
    val users = remember { mutableStateListOf<StoreUsersDTO>() }
    var usersTotalCount by remember { mutableStateOf(0) }
    var usersCurrentPage by remember { mutableStateOf(1) }
    var usersBasicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var usersSelectedStatus by remember { mutableStateOf(STORE_USERS_STATUS.ACTIVE) }
    var triggerUsersRefresh by remember { mutableStateOf(false) }

    /* All Invites */
    val invites = remember { mutableStateListOf<StoreUsersInviteDTO>() }
    var invitesTotalCount by remember { mutableStateOf(0) }
    var invitesCurrentPage by remember { mutableStateOf(1) }
    var invitesBasicFilters by remember { mutableStateOf(BasicTableFilters()) }
    var triggerInvitesRefresh by remember { mutableStateOf(false) }

    LaunchedEffect(triggerUsersRefresh, usersCurrentPage, usersBasicFilters, usersSelectedStatus) {
        adminFetchAllStoreUsers(
            pageNumber = usersCurrentPage,
            pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT,
            storeStatus = usersSelectedStatus,
            basicFilters = usersBasicFilters,
            onResponse = { response ->
                if (response.success && response.data != null) {
                    users.clear()
                    users.addAll(response.data.items)
                    usersTotalCount = response.data.totalCount
                    if (users.isEmpty()) usersCurrentPage = 1
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading users!", status = 0))
                }
            },
            onError = {
                alertManager.addAlert(UserAlert(message = "Error loading users!", status = 0))
            }
        )
    }

    LaunchedEffect(triggerInvitesRefresh, invitesCurrentPage, invitesBasicFilters) {
        adminFetchStoreUserInvites(
            pageNumber = invitesCurrentPage,
            pageSize = Constants.ADMIN_TABLE_SIZE_DEFAULT,
            basicFilters = invitesBasicFilters,
            onResponse = { response ->
                if (response.success && response.data != null) {
                    invites.clear()
                    invites.addAll(response.data.items)
                    invitesTotalCount = response.data.totalCount
                    if (invites.isEmpty()) invitesCurrentPage = 1
                } else {
                    alertManager.addAlert(UserAlert(message = "Error loading invites!", status = 0))
                }
            },
            onError = {
                alertManager.addAlert(UserAlert(message = "Error loading invites!", status = 0))
            }
        )
    }

    Column(
        modifier = Modifier
            .padding(24.px)
            .backgroundColor(Theme.HalfWhite.rgb)
            .fillMaxSize()
    ) {
        NavBreadcrumb(ctx = context)

        Ul(attrs = Modifier.classNames("nav", "nav-tabs").margin(bottom = 16.px).toAttrs()) {
            Li(attrs = Modifier.classNames("nav-item").toAttrs()) {
                Button(
                    attrs = Modifier
                        .classNames("nav-link", if (activeTab == "users") "active" else "notactive")
                        .onClick { activeTab = "users" }
                        .toAttrs()
                ) { Text("Store Users") }
            }
            Li(attrs = Modifier.classNames("nav-item").toAttrs()) {
                Button(
                    attrs = Modifier
                        .classNames("nav-link", if (activeTab == "invites") "active" else "notactive")
                        .onClick { activeTab = "invites" }
                        .toAttrs()
                ) { Text("Invites") }
            }
        }

        if (activeTab == "users") {
            AdminStoreUsersPanel(
                alertManager = alertManager,
                users = users,
                totalCount = usersTotalCount,
                currentPage = usersCurrentPage,
                basicFilters = usersBasicFilters,
                selectedStatus = usersSelectedStatus,
                onPageChange = { usersCurrentPage = it },
                onFiltersChange = { usersBasicFilters = it; usersCurrentPage = 1 },
                onStatusChange = { usersSelectedStatus = it; usersCurrentPage = 1 },
                onUpdateUser = { dto ->
                    scope.launch {
                        adminUpdateStoreUser(
                            updatedStoreUsersDTO = dto,
                            onSuccess = { response ->
                                if (response.success) {
                                    triggerUsersRefresh = !triggerUsersRefresh
                                    alertManager.addAlert(UserAlert(message = "User Updated.", status = 2))
                                } else {
                                    alertManager.addAlert(UserAlert(message = response.error?.message ?: "Error Updating User.", status = 0))
                                }
                            },
                            onError = {
                                alertManager.addAlert(UserAlert(message = "Error Updating User.", status = 0))
                            }
                        )
                    }
                },
                onDeleteUser = { user ->
                    scope.launch {
                        adminDeleteStoreUser(
                            storeId = user.storeId,
                            userUuid = user.uuid,
                            onSuccess = { response ->
                                if (response.success) {
                                    triggerUsersRefresh = !triggerUsersRefresh
                                    alertManager.addAlert(UserAlert(message = "User deleted.", status = 2))
                                } else {
                                    alertManager.addAlert(UserAlert(message = response.error?.message ?: "Error deleting user.", status = 0))
                                }
                            },
                            onError = {
                                alertManager.addAlert(UserAlert(message = "Error deleting user.", status = 0))
                            }
                        )
                    }
                },
                onEditUserStatus = { user, newStatus ->
                    scope.launch {
                        adminUpdateStoreUserStatus(
                            storeId = user.storeId,
                            userUuid = user.uuid,
                            status = newStatus,
                            onSuccess = { response ->
                                if (response.success) {
                                    triggerUsersRefresh = !triggerUsersRefresh
                                    alertManager.addAlert(UserAlert(message = "User Status Updated.", status = 2))
                                } else {
                                    alertManager.addAlert(
                                        UserAlert(message = response.error?.message ?: "Error Updating User status!", status = 0)
                                    )
                                }
                            },
                            onError = {
                                alertManager.addAlert(UserAlert(message = "Error Updating User status. :(", status = 0))
                            },
                        )
                    }
                },
                filterId = "adminUsersPageFilters"
            )
        }

        if (activeTab == "invites") {
            AdminStoreInvitesPanel(
                alertManager = alertManager,
                invites = invites,
                totalCount = invitesTotalCount,
                currentPage = invitesCurrentPage,
                basicFilters = invitesBasicFilters,
                onPageChange = { invitesCurrentPage = it },
                onFiltersChange = { invitesBasicFilters = it; invitesCurrentPage = 1 },
                onInviteCreated = { triggerInvitesRefresh = !triggerInvitesRefresh },
                onCancelInvite = { invite ->
                    scope.launch {
                        adminCancelStoreUserInvite(
                            storeId = invite.storeId,
                            inviteUuid = invite.uuid,
                            onSuccess = { response ->
                                if (response.success) {
                                    triggerInvitesRefresh = !triggerInvitesRefresh
                                    alertManager.addAlert(UserAlert(message = "Invite cancelled.", status = 2))
                                } else {
                                    alertManager.addAlert(UserAlert(message = response.error?.message ?: "Error cancelling invite.", status = 0))
                                }
                            },
                            onError = {
                                alertManager.addAlert(UserAlert(message = "Error cancelling invite.", status = 0))
                            }
                        )
                    }
                },
                filterId = "adminInvitesPageFilters"
            )
        }
    }
}
